import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiCalendar, FiRefreshCw } from 'react-icons/fi';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Funnel,
  FunnelChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { fetchDashboardSummary } from '../api/dashboardService';

function toTitle(value = '') {
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function FunnelStageTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;

  return (
    <div className="dashboard-funnel-tooltip">
      <p>{point.stage}</p>
      <strong>{point.value} leads</strong>
    </div>
  );
}

function DashboardPage() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ from: '', to: '' });
  const [isTimeWindowOpen, setIsTimeWindowOpen] = useState(false);

  const loadSummary = useCallback(async (params = {}) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchDashboardSummary(params);
      setSummary(res.data || res);
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to load dashboard data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const onFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const onApplyFilters = (e) => {
    e.preventDefault();
    const params = {};
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;
    setIsTimeWindowOpen(false);
    loadSummary(params);
  };

  const onClearFilters = () => {
    setFilters({ from: '', to: '' });
    setIsTimeWindowOpen(false);
    loadSummary();
  };

  const kpis = useMemo(() => summary?.kpis ?? {}, [summary]);
  const priorityQueue = useMemo(() => summary?.top_priority_queue ?? [], [summary]);
  const sourceBreakdown = useMemo(() => summary?.source_breakdown ?? [], [summary]);
  const channelBreakdown = useMemo(() => summary?.channel_breakdown ?? [], [summary]);
  const recentActivity = useMemo(() => summary?.recent_activity ?? [], [summary]);
  const priorityDist = useMemo(() => summary?.priority_distribution ?? [], [summary]);
  const tempDist = useMemo(() => summary?.temperature_distribution ?? [], [summary]);
  const leadStats = useMemo(() => summary?.leads ?? {}, [summary]);
  const activeQueues = useMemo(() => summary?.active_queues ?? [], [summary]);

  const totalLeads = kpis.total_leads ?? leadStats.total ?? 0;
  const newLeads = kpis.new_leads ?? leadStats.new_this_week ?? 0;
  const assignedLeads = kpis.assigned_leads ?? leadStats.assigned ?? 0;
  const inProgressLeads = kpis.in_progress_leads ?? leadStats.in_progress ?? 0;
  const convertedLeads = kpis.converted_leads ?? leadStats.converted ?? 0;
  const lostRejectedLeads = kpis.lost_or_rejected_leads ?? leadStats.lost_or_rejected ?? 0;
  const unassignedLeads = kpis.unassigned_leads ?? leadStats.unassigned ?? 0;
  const reassignedLeads = kpis.reassigned_leads ?? leadStats.reassigned_leads ?? 0;
  const agentOwnedLeads = kpis.agent_owned ?? leadStats.agent_owned ?? 0;
  const queueOwnedLeads = kpis.queue_owned ?? leadStats.queue_owned ?? 0;
  const trulyUnassignedLeads = kpis.truly_unassigned ?? leadStats.truly_unassigned ?? unassignedLeads;
  const slaBreachedLeads = kpis.sla_breached ?? leadStats.sla_breached ?? 0;
  const activeQueueCount = activeQueues.length;

  const conversionRate = totalLeads ? Math.round((convertedLeads / totalLeads) * 100) : 0;
  const lossRate = totalLeads ? Math.round((lostRejectedLeads / totalLeads) * 100) : 0;
  const activePipeline = newLeads + assignedLeads + inProgressLeads;
  const assignmentCoverage = totalLeads ? Math.round(((totalLeads - unassignedLeads) / totalLeads) * 100) : 0;
  const reassignmentRate = totalLeads ? Math.round((reassignedLeads / totalLeads) * 100) : 0;
  const pipelineVelocity = activePipeline ? Math.round((convertedLeads / activePipeline) * 100) : 0;

  const avgLeadScore = useMemo(() => {
    const scores = priorityQueue
      .map((item) => Number(item.lead_score))
      .filter((value) => Number.isFinite(value));
    if (!scores.length) return null;
    const avg = scores.reduce((sum, value) => sum + value, 0) / scores.length;
    return Math.round(avg);
  }, [priorityQueue]);

  const sourceTotal = sourceBreakdown.reduce((sum, item) => sum + (item.count ?? 0), 0);

  const topSource = useMemo(() => {
    if (!sourceBreakdown.length) return null;
    return sourceBreakdown.reduce((best, item) => ((item.count ?? 0) > (best.count ?? 0) ? item : best));
  }, [sourceBreakdown]);

  const riskFlags = useMemo(() => {
    const flags = [];
    if (trulyUnassignedLeads > 0) {
      flags.push({
        level: trulyUnassignedLeads >= 15 ? 'high' : 'medium',
        title: 'Truly unassigned lead backlog',
        description: `${trulyUnassignedLeads} leads have no owner at all. This is direct response-time risk.`,
      });
    }
    if (slaBreachedLeads > 0) {
      flags.push({
        level: slaBreachedLeads >= 5 ? 'high' : 'medium',
        title: 'SLA breaches detected',
        description: `${slaBreachedLeads} queued leads are already past SLA and need intervention now.`,
      });
    }
    if (queueOwnedLeads >= 20) {
      flags.push({
        level: 'medium',
        title: 'Queue backlog building',
        description: `${queueOwnedLeads} leads are parked in queues. Review dispatch cadence and staffing coverage.`,
      });
    }
    if (lossRate >= 25) {
      flags.push({
        level: 'high',
        title: 'Loss rate is elevated',
        description: `${lossRate}% of leads are in lost/rejected state. Check source quality and qualification criteria.`,
      });
    }
    if (reassignmentRate >= 20) {
      flags.push({
        level: 'medium',
        title: 'High reassignment churn',
        description: `${reassignmentRate}% reassignment indicates unstable ownership or capacity imbalance.`,
      });
    }
    if (!flags.length) {
      flags.push({
        level: 'low',
        title: 'No major operational risks detected',
        description: 'Current conversion, assignment, and ownership indicators are within expected bounds.',
      });
    }
    return flags;
  }, [lossRate, queueOwnedLeads, reassignmentRate, slaBreachedLeads, trulyUnassignedLeads]);

  const stagePipeline = [
    { label: 'New', value: newLeads, tone: 'sky' },
    { label: 'Assigned', value: assignedLeads, tone: 'indigo' },
    { label: 'In Progress', value: inProgressLeads, tone: 'amber' },
    { label: 'Converted', value: convertedLeads, tone: 'emerald' },
    { label: 'Lost / Rejected', value: lostRejectedLeads, tone: 'rose' },
  ];

  const funnelColors = {
    sky: '#38bdf8',
    indigo: '#6366f1',
    amber: '#f59e0b',
    emerald: '#10b981',
    rose: '#f43f5e',
  };

  const funnelData = stagePipeline
    .filter((stage) => stage.value > 0)
    .map((stage) => ({
      stage: stage.label,
      value: stage.value,
      color: funnelColors[stage.tone] || '#94a3b8',
    }));

  const funnelLegend = stagePipeline.map((stage) => ({
    label: stage.label,
    color: funnelColors[stage.tone] || '#94a3b8',
  }));

  const sourceChartData = [...sourceBreakdown]
    .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
    .slice(0, 6)
    .map((item) => ({
      source: item.source,
      count: item.count ?? 0,
    }));

  return (
    <div className="dashboard-page">
      <div className="page-title-row dashboard-hero">
        <div>
          <h2>Dashboard</h2>
          <p className="dashboard-copy">
            Operational view of funnel health, ownership risk, and conversion outcomes using live
            backend metrics.
          </p>
        </div>
        <div className="dashboard-hero-actions">
          <Link className="primary-link" to="/leads/new">Add New Lead</Link>
          <Link className="ghost-btn" to="/reports">View Reports</Link>
        </div>
      </div>

      {loading ? <p>Loading dashboard…</p> : null}
      {error ? <p className="error-text">{error}</p> : null}

      {!loading && !error && summary ? (
        <>
          {/* KPIs */}
          <div className="dashboard-kpis">
            <article className="dashboard-kpi-card dashboard-kpi-card--volume">
              <span>Total Leads</span>
              <strong>{totalLeads}</strong>
              <small>All records in the system</small>
            </article>
            <article className="dashboard-kpi-card dashboard-kpi-card--intake">
              <span>New Intake</span>
              <strong>{newLeads}</strong>
              <small>Fresh leads awaiting first touch</small>
            </article>
            <article className="dashboard-kpi-card dashboard-kpi-card--pipeline">
              <span>Active Pipeline</span>
              <strong>{activePipeline}</strong>
              <small>Leads actively being worked</small>
            </article>
            <article className="dashboard-kpi-card dashboard-kpi-card--conversion">
              <span>Conversion Rate</span>
              <strong>{conversionRate}%</strong>
              <small>{convertedLeads} converted opportunities</small>
            </article>
            <article className="dashboard-kpi-card dashboard-kpi-card--coverage">
              <span>Assignment Coverage</span>
              <strong>{assignmentCoverage}%</strong>
              <small>{trulyUnassignedLeads} leads are still truly unassigned</small>
            </article>
            <article className="dashboard-kpi-card dashboard-kpi-card--velocity">
              <span>Pipeline Velocity</span>
              <strong>{pipelineVelocity}%</strong>
              <small>Converted vs open active pipeline</small>
            </article>
            <article className="dashboard-kpi-card dashboard-kpi-card--pipeline">
              <span>Agent-Owned Leads</span>
              <strong>{agentOwnedLeads}</strong>
              <small>Leads actively owned by agents</small>
            </article>
            <article className="dashboard-kpi-card dashboard-kpi-card--intake">
              <span>In Queue</span>
              <strong>{queueOwnedLeads}</strong>
              <small>Leads waiting in queue ownership</small>
            </article>
            <article className="dashboard-kpi-card dashboard-kpi-card--conversion">
              <span>SLA Breached</span>
              <strong>{slaBreachedLeads}</strong>
              <small>Queued leads past due now</small>
            </article>
            <article className="dashboard-kpi-card dashboard-kpi-card--volume">
              <span>Active Queues</span>
              <strong>{activeQueueCount}</strong>
              <small>Queues currently tracked in dashboard health</small>
            </article>
          </div>

          <div className="dashboard-inline-tools">
            <div className="dashboard-timewindow-wrap">
              <button
                className="dashboard-icon-btn"
                type="button"
                onClick={() => setIsTimeWindowOpen((prev) => !prev)}
                aria-expanded={isTimeWindowOpen}
                title="Time window"
              >
                <FiCalendar />
              </button>
              {isTimeWindowOpen ? (
                <form className="dashboard-filter-popover" onSubmit={onApplyFilters}>
                  <label>
                    From
                    <input type="date" name="from" value={filters.from} onChange={onFilterChange} />
                  </label>
                  <label>
                    To
                    <input type="date" name="to" value={filters.to} onChange={onFilterChange} />
                  </label>
                  <div className="dashboard-filter-actions">
                    <button className="primary-btn" type="submit">Apply</button>
                    <button className="ghost-btn" type="button" onClick={onClearFilters}>Clear</button>
                  </div>
                </form>
              ) : null}
            </div>

            <button className="dashboard-icon-btn" type="button" onClick={() => loadSummary()} title="Refresh metrics">
              <FiRefreshCw />
            </button>
          </div>

          {/* Pipeline + Risk intelligence */}
          <div className="dashboard-grid dashboard-grid--two-thirds">
            <section className="dashboard-panel">
              <div className="panel-heading-row">
                <div>
                  <h3>Pipeline Throughput</h3>
                  <p>Stage load with proportional share of total funnel volume.</p>
                </div>
                <Link className="panel-link" to="/leads">Open pipeline</Link>
              </div>
              <div className="stage-grid dashboard-stage-grid-rich">
                {stagePipeline.map((stage) => (
                  <article key={stage.label} className={`stage-card stage-card--${stage.tone}`}>
                    <span>{stage.label}</span>
                    <strong>{stage.value}</strong>
                    <div className="progress-track">
                      <div
                        className="progress-fill"
                        style={{ width: `${totalLeads ? Math.round((stage.value / totalLeads) * 100) : 0}%` }}
                      />
                    </div>
                    <small>{totalLeads ? Math.round((stage.value / totalLeads) * 100) : 0}% of total</small>
                  </article>
                ))}
              </div>
            </section>

            <section className="dashboard-panel">
              <div className="panel-heading-row">
                <div>
                  <h3>Risk Intelligence</h3>
                  <p>Actionable signals that impact response SLA and close probability.</p>
                </div>
              </div>
              <div className="dashboard-alert-list">
                {riskFlags.map((flag, idx) => (
                  <article key={flag.title + idx} className={`dashboard-alert dashboard-alert--${flag.level}`}>
                    <strong>{flag.title}</strong>
                    <p>{flag.description}</p>
                  </article>
                ))}
              </div>

              <div className="ops-grid" style={{ marginTop: 14 }}>
                <article className="ops-card">
                  <span>Loss Rate</span>
                  <strong>{lossRate}%</strong>
                </article>
                <article className="ops-card">
                  <span>Reassignment Rate</span>
                  <strong>{reassignmentRate}%</strong>
                </article>
                <article className="ops-card">
                  <span>Queue-Owned</span>
                  <strong>{queueOwnedLeads}</strong>
                </article>
                <article className="ops-card">
                  <span>SLA Breached</span>
                  <strong>{slaBreachedLeads}</strong>
                </article>
              </div>

              {tempDist.length > 0 && (
                <div className="temp-buckets">
                  {tempDist.map((t) => (
                    <div key={t.bucket} className="temp-pill">
                      <span className={`temp-dot temp-dot--${t.bucket}`} />
                      <span>{t.bucket.charAt(0).toUpperCase() + t.bucket.slice(1)}</span>
                      <strong>{t.count}</strong>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>

          <div className="dashboard-grid dashboard-grid--split">
            <section className="dashboard-panel">
              <div className="panel-heading-row">
                <div>
                  <h3>Queue Health</h3>
                  <p>Queue volume, SLA windows, and breach counts from the assignment engine.</p>
                </div>
                <Link className="panel-link" to="/queue-worklist">Open queue worklist</Link>
              </div>
              <div className="dashboard-table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Queue</th>
                      <th>Current Size</th>
                      <th>SLA Hours</th>
                      <th>SLA Breached</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeQueues.length ? (
                      activeQueues.map((queue) => (
                        <tr key={queue.id}>
                          <td>{queue.name}</td>
                          <td>{queue.current_size ?? 0}</td>
                          <td>{queue.sla_hours ?? '—'}</td>
                          <td>{queue.sla_breached_count ?? 0}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="4">No queue health metrics available.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="dashboard-panel">
              <div className="panel-heading-row">
                <div>
                  <h3>Assignment Snapshot</h3>
                  <p>Ownership balance across agents, queues, and truly unassigned leads.</p>
                </div>
                <Link className="panel-link" to="/leads">Open leads</Link>
              </div>
              <div className="ops-grid">
                <article className="ops-card">
                  <span>Agent-Owned</span>
                  <strong>{agentOwnedLeads}</strong>
                </article>
                <article className="ops-card">
                  <span>Queue-Owned</span>
                  <strong>{queueOwnedLeads}</strong>
                </article>
                <article className="ops-card">
                  <span>Truly Unassigned</span>
                  <strong>{trulyUnassignedLeads}</strong>
                </article>
                <article className="ops-card">
                  <span>Active Queues</span>
                  <strong>{activeQueueCount}</strong>
                </article>
              </div>
            </section>
          </div>

          {/* Work queue + Source intelligence */}
          <div className="dashboard-grid dashboard-grid--split">
            <section className="dashboard-panel">
              <div className="panel-heading-row">
                <div>
                  <h3>Priority Work Queue</h3>
                  <p>Highest-value leads to act on now, sorted by urgency and score.</p>
                </div>
                {avgLeadScore !== null ? <span className="dashboard-chip">Avg score: {avgLeadScore}</span> : null}
              </div>
              <div className="dashboard-table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Lead</th>
                      <th>Priority</th>
                      <th>Score</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {priorityQueue.length ? (
                      priorityQueue.map((lead) => (
                        <tr key={lead.id}>
                          <td>{lead.full_name || '—'}</td>
                          <td>{lead.priority || '—'}</td>
                          <td>{lead.lead_score ?? '—'}</td>
                          <td>{lead.status || '—'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="4">No priority leads yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="dashboard-panel">
              <div className="panel-heading-row">
                <div>
                  <h3>Source Intelligence</h3>
                  <p>Source-level contribution to total acquisition volume.</p>
                </div>
                {topSource ? <span className="dashboard-chip">Top: {topSource.source}</span> : null}
              </div>
              <div className="source-list">
                {sourceBreakdown.length ? (
                  sourceBreakdown.map((item) => (
                    <div key={item.source} className="source-row">
                      <span>{item.source}</span>
                      <div className="source-row-metrics">
                        <small>{sourceTotal ? Math.round((item.count / sourceTotal) * 100) : 0}%</small>
                        <strong>{item.count}</strong>
                      </div>
                    </div>
                  ))
                ) : (
                  <p>No source data yet.</p>
                )}
              </div>
              {channelBreakdown.length > 0 && (
                <>
                  <h4 className="sub-heading">Channels</h4>
                  <div className="source-list">
                    {channelBreakdown.map((item) => (
                      <div key={item.channel} className="source-row">
                        <span>{item.channel}</span>
                        <strong>{item.count}</strong>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </section>
          </div>

          {/* Activity + Priority distribution */}
          <div className="dashboard-grid dashboard-grid--split">
            <section className="dashboard-panel">
              <div className="panel-heading-row">
                <div>
                  <h3>Recent Execution Activity</h3>
                  <p>Latest recorded transitions and workflow events.</p>
                </div>
                <Link className="panel-link" to="/audit-log">Open audit log</Link>
              </div>
              <div className="activity-feed">
                {recentActivity.length ? (
                  recentActivity.map((item, idx) => (
                    <article key={item.lead_id + idx} className="activity-item">
                      <div>
                        <strong>{toTitle(item.event || 'event')}</strong>
                        {item.to_status && <p>→ {item.to_status}</p>}
                      </div>
                      <span>{formatDateTime(item.at)}</span>
                    </article>
                  ))
                ) : (
                  <p>No recent activity yet.</p>
                )}
              </div>
            </section>

            <section className="dashboard-panel">
              <div className="panel-heading-row">
                <div>
                  <h3>Priority Distribution</h3>
                  <p>Demand pressure by urgency tier.</p>
                </div>
              </div>
              <div className="source-list">
                {priorityDist.length ? (
                  priorityDist.map((item) => (
                    <div key={item.priority} className="source-row">
                      <span>{item.priority}</span>
                      <strong>{item.count}</strong>
                    </div>
                  ))
                ) : (
                  <p>No priority data yet.</p>
                )}
              </div>
            </section>
          </div>

          <section className="dashboard-panel">
            <div className="panel-heading-row">
              <div>
                <h3>Visual Intelligence</h3>
                <p>Stage-wise funnel conversion and top source contribution at a glance.</p>
              </div>
            </div>

            <div className="dashboard-chart-grid">
              <article className="dashboard-chart-card">
                <h4>Pipeline Funnel</h4>
                <p>Shows how lead volume narrows through each pipeline stage.</p>
                {funnelData.length ? (
                  <div className="dashboard-chart-area">
                    <ResponsiveContainer width="100%" height={290}>
                      <FunnelChart>
                        <Tooltip content={<FunnelStageTooltip />} />
                        <Funnel dataKey="value" data={funnelData} isAnimationActive>
                          {funnelData.map((entry) => (
                            <Cell key={entry.stage} fill={entry.color} />
                          ))}
                        </Funnel>
                      </FunnelChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="dashboard-chart-empty">No stage data available for funnel.</p>
                )}
                <div className="dashboard-funnel-legend" aria-label="Funnel stage color legend">
                  {funnelLegend.map((item) => (
                    <div key={item.label} className="dashboard-funnel-legend-item">
                      <span className="dashboard-funnel-swatch" style={{ backgroundColor: item.color }} />
                      <span>{item.label}</span>
                    </div>
                  ))}
                </div>
              </article>

              <article className="dashboard-chart-card">
                <h4>Top Lead Sources</h4>
                <p>Compares the highest contributing sources by lead count.</p>
                {sourceChartData.length ? (
                  <div className="dashboard-chart-area">
                    <ResponsiveContainer width="100%" height={290}>
                      <BarChart data={sourceChartData} margin={{ top: 12, right: 8, left: 8, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="source" tick={{ fontSize: 12 }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(value) => [`${value}`, 'Leads']} />
                        <Bar dataKey="count" fill="#6366f1" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="dashboard-chart-empty">No source data available for chart.</p>
                )}
              </article>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}

export default DashboardPage;
